import React, { useEffect, useState, useRef } from 'react';
import { CircularProgress, Container, Typography, Alert, Chip } from '@mui/material';
import MatchedUsersList from '../components/MatchedUsersList';
import { useNavigate } from 'react-router-dom';
import { BASE_URL } from '../config';
import { useAxios } from '../hooks/useAxios';
import SockJS from 'sockjs-client';
import { Stomp } from '@stomp/stompjs';
import { useAuth } from '../auth/AuthContext';
import { getUserId } from '../utils/auth';

export type MatchedUser = {
  matchedUserId: string;
  firstName: string;
  profilePhotoId: string | null;
  lastMessage: string | null;
  lastMessageDateTime: string | null;
  lastMessageRead?: boolean;
  isTyping?: boolean;
};

const MatchedUsersPage: React.FC = () => {
  const [matchedUsers, setMatchedUsers] = useState<MatchedUser[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const axiosInstance = useAxios();
  const { token, refresh } = useAuth();
  const userId = getUserId() || '';
  const clientRef = useRef<any>(null);
  const typingTimeouts = useRef<{ [key: string]: NodeJS.Timeout }>({});

  const isAccessTokenValid = (accessToken: string | null): boolean => {
    if (!accessToken) return false;
    try {
      const payload = JSON.parse(atob(accessToken.split('.')[1]));
      const now = Math.floor(Date.now() / 1000);
      return payload.exp > now;
    } catch {
      return false;
    }
  };

  const connectWebSocket = async () => {
    let tokenToUse = isAccessTokenValid(token) ? token : null;
    if (!tokenToUse && refresh) {
      const refreshed = await refresh();
      if (refreshed) {
        tokenToUse = localStorage.getItem('accessToken');
      }
    }

    if (!tokenToUse) {
      console.error('No valid token. WebSocket cannot connect.');
      return;
    }

    const socket = new SockJS(`${BASE_URL}/ws?access_token=${tokenToUse}`);
    const client = Stomp.over(socket);
    client.debug = () => { };
    client.reconnectDelay = 5000;
    client.heartbeatIncoming = 10000;
    client.heartbeatOutgoing = 10000;

    clientRef.current = client;

    client.connect(
      { Authorization: `Bearer ${tokenToUse}` },
      () => {
        // Yeni mesaj geldiğinde
        client.subscribe(`/user/queue/messages`, (msg) => {
          const message = JSON.parse(msg.body);
          updateMatchedUsersWithNewMessage(message);
        });

        // Mesaj okundu bilgisi geldiğinde
        client.subscribe(`/user/queue/messages-read`, (msg) => {
          const { senderId } = JSON.parse(msg.body);
          updateReadStatus(senderId);
        });

        // Typing durumu geldiğinde
        client.subscribe(`/user/queue/typing`, (msg) => {
          const { senderId, typing } = JSON.parse(msg.body);
          handleTypingStatus(senderId, typing);
        });
      },
      async (error: any) => {
        console.error('WebSocket connection error:', error);
        if (refresh) {
          const refreshed = await refresh();
          if (refreshed) {
            setTimeout(() => connectWebSocket(), 1000);
          }
        }
      }
    );
  };

  const handleTypingStatus = (senderId: string, typing: boolean) => {
    setMatchedUsers(prevUsers => {
      return prevUsers.map(user => {
        if (user.matchedUserId === senderId) {
          // Önceki timeout'u temizle
          if (typingTimeouts.current[senderId]) {
            clearTimeout(typingTimeouts.current[senderId]);
          }

          // Yeni timeout ayarla (3 saniye sonra typing durumunu false yap)
          if (typing) {
            typingTimeouts.current[senderId] = setTimeout(() => {
              setMatchedUsers(prev => prev.map(u =>
                u.matchedUserId === senderId ? { ...u, isTyping: false } : u
              ));
            }, 3000);
          }

          return {
            ...user,
            isTyping: typing
          };
        }
        return user;
      });
    });
  };

const updateMatchedUsersWithNewMessage = (message: any) => {
  const isIncoming = message.senderUser !== userId;
  const targetUserId = isIncoming ? message.senderUser : message.receiverUser;

  setMatchedUsers(prevUsers => {
    return prevUsers.map(user => {
      if (user.matchedUserId === targetUserId) {
        // Typing durumunu kapat
        if (typingTimeouts.current[targetUserId]) {
          clearTimeout(typingTimeouts.current[targetUserId]);
          delete typingTimeouts.current[targetUserId];
        }

        return {
          ...user,
          lastMessage: message.content,
          lastMessageDateTime: message.sendDateTime,
          lastMessageRead: isIncoming ? message.read : true, // Kendi mesajlarımız direkt okundu
          isTyping: false,
          isLastMessageFromMe: !isIncoming
        };
      }
      return user;
    }).sort((a, b) => {
      const dateA = a.lastMessageDateTime ? new Date(a.lastMessageDateTime).getTime() : 0;
      const dateB = b.lastMessageDateTime ? new Date(b.lastMessageDateTime).getTime() : 0;
      return dateB - dateA;
    });
  });
};

  const updateReadStatus = (senderId: string) => {
    setMatchedUsers(prevUsers =>
      prevUsers.map(user => {
        if (user.matchedUserId === senderId) {
          return {
            ...user,
            lastMessageRead: true
          };
        }
        return user;
      })
    );
  };

const fetchUsers = async () => {
  try {
    const res = await axiosInstance.get('/api/user/matched-users');
    if (res.data.status === 'ok') {
      const usersWithStatus = await Promise.all(
        res.data.data.map(async (user: MatchedUser) => {
          // Son mesajın kimden geldiğini ve okunma durumunu al
          const messageRes = await axiosInstance.get(
            `/api/user/conversation/${user.matchedUserId}?page=0&size=1`
          );
          const lastMessage = messageRes.data.data[0];
          
          return {
            ...user,
            lastMessageRead: lastMessage 
              ? lastMessage.senderUser === userId || lastMessage.read 
              : true,
            isLastMessageFromMe: lastMessage?.senderUser === userId
          };
        })
      );

      // Sıralama
      const sortedUsers = usersWithStatus.sort((a, b) => {
        const dateA = a.lastMessageDateTime ? new Date(a.lastMessageDateTime).getTime() : 0;
        const dateB = b.lastMessageDateTime ? new Date(b.lastMessageDateTime).getTime() : 0;
        return dateB - dateA;
      });

      setMatchedUsers(sortedUsers);
    } else {
      setError('Kullanıcılar getirilemedi.');
    }
  } catch (err: any) {
    setError('Sunucu hatası.');
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    fetchUsers();
    connectWebSocket();

    return () => {
      if (clientRef.current && clientRef.current.connected) {
        clientRef.current.disconnect();
      }
      // Tüm timeout'ları temizle
      Object.values(typingTimeouts.current).forEach(timeout => clearTimeout(timeout));
    };
  }, []);

  const handleChatStart = (matchedUser: MatchedUser) => {
    // Mesajı okundu olarak işaretle
    setMatchedUsers(prevUsers =>
      prevUsers.map(user =>
        user.matchedUserId === matchedUser.matchedUserId
          ? { ...user, lastMessageRead: true }
          : user
      )
    );

    navigate(`/messages/${matchedUser.matchedUserId}`, { state: { matchedUser } });
  };

  return (
    <Container sx={{ mt: 4 }}>
      <Typography variant="h5" gutterBottom>
        Messages
      </Typography>

      {loading && <CircularProgress />}
      {error && <Alert severity="error">{error}</Alert>}

      {!loading && !error && (
        <MatchedUsersList
          users={matchedUsers}
          baseURL={BASE_URL}
          onChatStart={handleChatStart}
        />
      )}
    </Container>
  );
};

export default MatchedUsersPage;